const { trapFocus, disableTrapFocus, setCookie } = window.BAO.Utils
class FooterPopupNewsletter extends window.BAO.CustomElement() {
  static get requiredElements () {
    return [
      'form',
      'list',
      'email',
      'close',
      'logo',
      'subscribe-text',
      'subscribe-form',
      'error',
    ]
  }

  static get requiredExternalElements () {
    return ['modal']
  }

  constructor () {
    super()

    this.defaultProperties = {
      'Accepts Marketing': 'true',
      evergreen_step1_source: 'website_popup',
    }
    this.dailyStorageName = 'BAO-newsletter-daily-popup'
    this.submittedStorageName = 'BAO-newsletter-submitted-popup'
    this.teaserClosedStorageName = 'BAO-newsletter-teaser-closed'
    this.isCustomer = this.dataset.customer
    this.automaticPopupEnabled = this.dataset.auto
    this.bodyOpenClassName = 'lyt-Theme-popupOpen'

    this.toggleTabbableElements = this.toggleTabbableElements.bind(this)
  }

  connectedCallback () {
    super.connectedCallback()

    this.toggleTabbableElements()
  }

  setupListeners () {
    super.setupListeners()

    this.openNewsletterModal = this.openNewsletterModal.bind(this)
    this.handleNewsletterTrigger = this.handleNewsletterTrigger.bind(this)

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.onFormSubmit.bind(this),
    )

    this.listeners.add(
      this.els.email.element,
      'input',
      this.handleEmailInput.bind(this),
    )

    this.listeners.add(window, 'load', this.openNewsletterModal(false))
    this.listeners.add(document, 'click', this.handleNewsletterTrigger)

    this.els.close.elements.forEach(element => {
      this.listeners.add(element, 'click', this.handleModalClose.bind(this))
    })
    this.externalEls.close.elements.forEach(element => {
      this.listeners.add(element, 'click', this.handleModalClose.bind(this))
    })

    this.listeners.add(
      document.documentElement,
      'keydown',
      this.handleKeydown.bind(this),
    )

    this.externalEls['view-account']?.elements?.forEach(element => {
      this.listeners.add(element, 'click', this.handleAccountClick) // not bound to this
    })
  }

  handleEmailInput () {
    if (this.validEmail()) {
      this.els.email.element.setAttribute('aria-invalid', false)
    } else {
      this.els.email.element.setAttribute('aria-invalid', true)
    }
  }

  handleNewsletterTrigger (e) {
    if (e.target.dataset.footerPopupNewsletterExternalEl !== 'trigger') return

    this.openNewsletterModal(true)
  }

  openNewsletterModal (isTriggered) {
    // Open straight away if the trigger is clicked.
    let delay = isTriggered ? 0 : this.dataset.delay * 1000

    if (!isTriggered && !this.shouldShowPopup()) {
      return
    }

    // If the URL contains ?show_newsletter_form
    const url = new URL(window.location.href)
    const searchParams = url.searchParams
    const popupRegistered = searchParams.has('show_newsletter_form', true)
    if (popupRegistered) delay = 0

    setTimeout(() => {
      if (
        window.localStorage.getItem(this.teaserClosedStorageName) !== null &&
        !isTriggered
      ) {
        // If the user has closed the teaser during the delay, we don't show the popup
        return
      }

      this.externalEls.modal.element.setAttribute('aria-hidden', false)

      document.body.classList.add(this.bodyOpenClassName)

      if (!isTriggered) {
        window.localStorage.setItem(
          this.dailyStorageName,
          new Date().toLocaleDateString(),
        )
      }

      this.toggleTabbableElements(false)

      trapFocus(this.externalEls.modal.element)

      if (
        document.querySelector(
          `.sec-Newsletter_Popup > [data-footer-popup-newsletter-el="close"]`,
        )
      ) {
        window.setTimeout(
          () =>
            document
              .querySelector(
                `.sec-Newsletter_Popup > [data-footer-popup-newsletter-el="close"]`,
              )
              .focus(),
          300,
        )
      }
    }, delay)
  }

  handleModalClose () {
    this.externalEls.modal.element.setAttribute('aria-hidden', true)

    document.body.classList.remove(this.bodyOpenClassName)

    this.toggleTabbableElements(false)

    disableTrapFocus()

    if (this.externalEls?.trigger?.exists) {
      this.externalEls.trigger.element.focus()
    }
  }

  handleKeydown (e) {
    if (this.externalEls.modal.element.ariaHidden === 'true') return

    if (e.key !== 'Escape') return

    this.externalEls.modal.element.setAttribute('aria-hidden', true)

    if (this.externalEls.trigger?.exists) {
      this.externalEls.trigger.element.focus()
    }
  }

  async onFormSubmit (e) {
    e.preventDefault()

    if (!window.theme?.klaviyo) return

    const listId = this?.els?.list?.element?.value
    const emailAddress = this?.els?.email?.element?.value.replace(/\s/g, '')

    if (!this.validEmail()) {
      this.els.email.element.setAttribute('aria-invalid', true)
      this.els.email.element.focus()
      return
    }

    // Check if profile exists already before submitting.
    const profileExists = await this.checkProfileExists(emailAddress)

    // If profile already exists, skip to register form.
    if (profileExists) {
      this.log('Profile already exists')
      this.toggleErrorMessage(window.theme.strings.newsletterEmailInUse)
      return
    }

    this.createClientSubscription({
      emailAddress,
      listId,
    })
  }

  toggleTabbableElements (disableTabbing = true) {
    const isInModal = this.closest(
      '[data-footer-popup-newsletter-external-el="modal"]',
    )
    if (!isInModal) return

    const tabbableElements = this.querySelectorAll(
      'a[href], button, textarea, input[type="text"], input[type="password"], input[type="email"], input[type="radio"], input[type="checkbox"], select',
    )

    tabbableElements.forEach(element => {
      if (disableTabbing) {
        element.setAttribute('tabindex', '-1')
        return
      }
      element.removeAttribute('tabindex')
    })

    if (disableTabbing) {
      this.els.close.element.setAttribute('tabindex', '-1')
      this.externalEls.close?.element?.setAttribute('tabindex', '-1')
    } else {
      this.els.close.element.removeAttribute('tabindex')
      this.externalEls.close?.element?.removeAttribute('tabindex')
    }
  }

  // Klaviyo API Methods.
  async checkProfileExists (emailAddress) {
    const scriptUrl = 'https://self-portrait-klaviyo.vercel.app/api/identify'

    const data = {
      email: emailAddress,
      store: window.theme?.klaviyo?.store,
    }

    const response = await fetch(scriptUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    const jsonResponse = await response.json()

    return jsonResponse.registered ? jsonResponse.registered : false
  }

  async createClientSubscription (formData) {
    const { emailAddress, listId } = formData
    const properties = { ...this.defaultProperties }

    const attributes = {
      email: emailAddress,
      properties,
    }

    const bodyData = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes,
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    }

    const options = {
      method: 'POST',
      headers: {
        revision: window.theme.klaviyo.apiVersion,
        'content-type': 'application/json',
      },
      body: JSON.stringify(bodyData),
    }

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${window.theme.klaviyo.apiKey}`,
        options,
      )

      // Expected success code is 202, but the response body is empty.
      // This is expected according to the docs:
      // 'The request has been received but not yet acted upon. We return this status
      //  code for requests that were accepted but are processed asynchronously.'
      if (response.status === 202) {
        this.toggleErrorMessage()
        this.showSubscribedMessage()

        // Markdown 2025
        this.markdownSale()
      } else {
        // There is data returned when there is an error.
        // On success, the response body is empty.
        const jsonResponse = await response.json()

        if (jsonResponse.errors.length > 0) {
          // eslint-disable-next-line no-console
          const errorMessage = jsonResponse.errors[0].detail
            ? jsonResponse.errors[0].detail
            : window.theme.strings.newsletterGenericError
          this.toggleErrorMessage(errorMessage)
        }
      }
    } catch (error) {
      console.error(error)
      const errorMessage =
        error && error.detail
          ? error.detail
          : window.theme.strings.newsletterGenericError
      this.toggleErrorMessage(errorMessage)
    }
  }

  markdownSale () {
    if (!window.theme.saleMarkdownEnable) return

    this.setNewCustomerStorage()
    this.setExistingCustomerStorage()

    localStorage.setItem('single_show_thankyou', 'true')

    if (this.dataset.mdSaleRedirect) {
      window.location.href = this.dataset.mdSaleRedirect
    }
  }

  toggleErrorMessage (message = false) {
    const errorElement = this.els.error.element
    if (!message) {
      errorElement.setAttribute('aria-hidden', true)
    } else {
      errorElement.setAttribute('aria-hidden', false)
      errorElement.innerHTML = message
    }
  }

  showSubscribedMessage () {
    window.localStorage.setItem(this.submittedStorageName, true)

    if (this?.externalEls?.teaser?.exists) {
      this.externalEls.teaser.element.setAttribute('aria-hidden', 'true')
    }

    // Hide logo
    this.els.logo.element.setAttribute('aria-hidden', false)

    // Hide subscribe form
    this.els['subscribe-text'].element.setAttribute('aria-hidden', true)
    this.els['subscribe-form'].element.setAttribute('aria-hidden', true)

    // Show registered text
    this.els['subscribed-text'].element.setAttribute('aria-hidden', false)
  }

  validEmail () {
    const emailAddress = this?.els?.email?.element?.value.replace(/\s/g, '')

    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
    return emailRegex.test(emailAddress)
  }

  shouldShowPopup () {
    let shouldShow = true

    // Don't show if popup has already been shown today
    const lastPopupDate = window.localStorage.getItem(this.dailyStorageName)
    const today = new Date().toLocaleDateString()
    shouldShow = lastPopupDate !== today

    // Don't show if popup has already been submitted
    if (window.localStorage.getItem(this.submittedStorageName) !== null) {
      shouldShow = false
    }

    // Don't show if already logged in
    if (this.isCustomer === 'true') shouldShow = false

    // Don't show if automatic popup is disabled
    if (this.automaticPopupEnabled === 'false') shouldShow = false

    // If the URL contains ?popup=show_newsletter_form
    const url = new URL(window.location.href)
    const searchParams = url.searchParams
    const popupRegistered = searchParams.has('show_newsletter_form', true)
    if (popupRegistered) {
      shouldShow = true
    }

    return shouldShow
  }

  setExistingCustomerStorage () {
    setCookie(
      this.existingCustomerCookieName,
      'true',
      window.theme.saleExistingCustomerCookieExpiration || 7,
    )
  }

  setNewCustomerStorage () {
    setCookie(
      this.newCustomerCookieName,
      'true',
      window.theme.saleNewCustomerCookieExpiration || 1,
    )
  }

  handleAccountClick (event) {
    event.preventDefault()

    window.dataLayer.push({
      event: 'footerNewsletterAccountClick',
    })

    window.location.href = this.getAttribute('href')
  }

  get existingCustomerCookieName () {
    return (
      window.theme.saleExistingCustomerCookie || `BAO-md-2025-existingCustomer`
    )
  }

  get newCustomerCookieName () {
    return window.theme.saleNewCustomerCookie || `BAO-md-2025-newCustomer`
  }
}

if (!customElements.get('footer-popup-newsletter')) {
  customElements.define('footer-popup-newsletter', FooterPopupNewsletter)
}
